// Navigation model for the Streaming Console shell.
// icon = Font Awesome class (already bundled in the app).
// key  = stable id used for per-plan sidebar customization (admin → Plans).

export const USER_NAV = [
  {
    section: 'Broadcast',
    items: [
      { key: 'dashboard', title: 'Dashboard', icon: 'fa-gauge-high', to: '/stream-dashboard' },
      { key: 'streams', title: 'Live Streams', icon: 'fa-tower-broadcast', to: '/streams' },
      { key: 'create-stream', title: 'Create Stream', icon: 'fa-circle-plus', to: '/create-stream' },
      { key: 'switcher', title: 'Live Switcher', icon: 'fa-sliders', to: '/switcher' },
      { key: 'team', title: 'Team', icon: 'fa-users', to: '/team' },
      { key: 'multistream', title: 'Multistream', icon: 'fa-share-nodes', to: '/social-streaming' },
    ],
  },
  {
    section: 'Content',
    items: [
      { key: 'playout', title: 'Playout', icon: 'fa-calendar-days', to: '/playout' },
      {
        key: 'vod', title: 'VOD Library', icon: 'fa-film', to: '/vod/uploader',
        children: [
          { key: 'vod-uploader', title: 'Uploader', to: '/vod/uploader' },
          { key: 'vod-directories', title: 'Media Directories', to: '/vod/directories' },
          { key: 'vod-playlists', title: 'Playlists', to: '/vod/playlists' },
          { key: 'vod-schedules', title: 'Schedules & Failover', to: '/vod/schedules' },
        ],
      },
      { key: 'iptv', title: 'IPTV Channels', icon: 'fa-tv', to: '/iptv' },
    ],
  },
  {
    section: 'Grow',
    items: [
      { key: 'analytics', title: 'Analytics', icon: 'fa-chart-line', to: '/analytics' },
      { key: 'monetization', title: 'Monetization', icon: 'fa-money-bill-trend-up', to: '/monetization' },
      // NOT adminOnly — routes/domains.js already gates this by the
      // subscription's own allow_custom_domains for a real subscriber; it
      // was wrongly hidden from every non-admin regardless of plan. Visibility
      // is now handled the normal way, via /api/nav's entitlement check.
      { key: 'white-label', title: 'White-label', icon: 'fa-globe', to: '/domains' },
    ],
  },
  {
    section: 'Account',
    items: [
      {
        key: 'subscription', title: 'Subscription', icon: 'fa-credit-card', to: '/billing',
        children: [
          { key: 'billing', title: 'Billing & Invoices', to: '/billing' },
          { key: 'pricing', title: 'Plans & Pricing', to: '/pricing' },
        ],
      },
      {
        key: 'developer', title: 'Developer', icon: 'fa-code', to: '/developer', adminOnly: true,
        children: [
          { key: 'developer-overview', title: 'API Overview', to: '/developer' },
          { key: 'developer-keys', title: 'API Keys', to: '/developer/api-keys' },
        ],
      },
      { key: 'tickets', title: 'Support Tickets', icon: 'fa-headset', to: '/tickets' },
      { key: 'notifications', title: 'Notifications', icon: 'fa-bell', to: '/notifications' },
    ],
  },
];

export const ADMIN_NAV = [
  {
    section: 'Administration',
    items: [
      { title: 'Server Health', icon: 'fa-gauge-high', to: '/admin/system-health' },
      { title: 'Users', icon: 'fa-users', to: '/admin/users' },
      { title: 'Roles', icon: 'fa-user-shield', to: '/admin/roles' },
      { title: 'Ticket Queue', icon: 'fa-ticket', to: '/admin/tickets' },
      { title: 'Subscriptions', icon: 'fa-id-card-clip', to: '/admin/subscriptions' },
      { title: 'Admin Billing', icon: 'fa-file-invoice-dollar', to: '/admin/billing' },
      { title: 'Payment & Tax Reports', icon: 'fa-file-contract', to: '/admin/reports' },
      {
        title: 'Invoices', icon: 'fa-file-invoice', to: '/admin/invoices',
        children: [
          { title: 'All Invoices', to: '/admin/invoices' },
          { title: 'Billing & Tax Settings', to: '/admin/billing-settings' },
        ],
      },
      { title: 'Platform Analytics', icon: 'fa-chart-pie', to: '/admin/analytics' },
    ],
  },
  {
    section: 'Platform Settings',
    items: [
      {
        title: 'Infrastructure', icon: 'fa-server', to: '/admin/settings',
        children: [
          { title: 'System Settings', to: '/admin/settings' },
          { title: 'CDN Configuration', to: '/cdn' },
          { title: 'Custom Domains', to: '/domains' },
          { title: 'IPTV / CDN Domains', to: '/iptv' },
        ],
      },
      {
        title: 'Integrations', icon: 'fa-plug', to: '/admin/stripe-settings',
        children: [
          { title: 'Stripe', to: '/admin/stripe-settings' },
          { title: 'Social Login', to: '/admin/social-settings' },
          { title: 'Account Verification', to: '/admin/verification-settings' },
          { title: 'Email & SMTP', to: '/admin/email-settings' },
          { title: 'Twilio SMS', to: '/admin/twilio-settings' },
          { title: 'Monetization', to: '/admin/monetization' },
        ],
      },
      { title: 'SEO / GEO / AEO', icon: 'fa-magnifying-glass-chart', to: '/admin/seo-settings' },
    ],
  },
];

// Flat catalogue of the customizable (non-admin) sidebar entries — used by the
// admin plan editor to pick which ones a plan's subscribers can see.
// adminOnly items are never shown to subscribers, so they're left out.
export const NAV_CATALOG = USER_NAV.map((group) => ({
  section: group.section,
  items: group.items
    .filter((it) => !it.adminOnly)
    .map((it) => ({
      key: it.key,
      title: it.title,
      children: (it.children || []).map((c) => ({ key: c.key, title: c.title })),
    })),
})).filter((group) => group.items.length > 0);

// Every customizable key (top-level + children) — the "show everything" set.
export const ALL_NAV_KEYS = NAV_CATALOG.flatMap((g) =>
  g.items.flatMap((it) => [it.key, ...it.children.map((c) => c.key)]));

/**
 * Build the sidebar for a user.
 * @param role         'admin' → full menu, unfiltered.
 * @param allowedKeys  array of nav keys the plan permits, or null/undefined
 *                     → show everything (plan not customized).
 */
export const getNav = (role, allowedKeys) => {
  if (role === 'admin') return [...USER_NAV, ...ADMIN_NAV];

  // Non-admins never see adminOnly items (white-label, developer), regardless
  // of the plan's nav_items.
  const visibleForUser = (it) => !it.adminOnly;
  const usePlanFilter = Array.isArray(allowedKeys);
  const ok = usePlanFilter ? new Set(allowedKeys) : null;

  return USER_NAV.map((group) => {
    const items = group.items
      .filter(visibleForUser)
      .filter((it) => !usePlanFilter || ok.has(it.key))
      .map((it) => {
        if (!it.children) return it;
        const kids = usePlanFilter ? it.children.filter((c) => ok.has(c.key)) : it.children;
        return { ...it, children: kids };
      });
    return { ...group, items };
  }).filter((group) => group.items.length > 0);
};
